import { UNSAVED_CHANGES_MESSAGE } from '../../shared/components/unsaved-changes-dialog';
import type { GlobalAiCustomDashAssistContext } from './global-ai-assistant-context';

const getPathnameFromRoute = (path: string) => path.split(/[?#]/)[0] || '/';

type ConfirmGlobalAiAssistantRedirectArgs = {
  customDashContext: GlobalAiCustomDashAssistContext;
  currentPathname: string;
  targetPath?: string;
  /**
   * The unsaved-changes prompt. ASYNC by design: the production
   * implementation is the in-app UnsavedChangesDialog (a native
   * `window.confirm` can be suppressed by the browser after repeated dialogs,
   * returning `false` instantly with no user involvement — the invisible
   * "clicked and nothing happened" failure). Sync boolean returns are still
   * accepted for tests.
   */
  confirmLeave: (message: string) => boolean | Promise<boolean>;
};

type ConfirmGlobalAiAssistantRedirectResult =
  | { shouldContinue: false; skipGuard: false; discardChanges?: undefined }
  | { shouldContinue: true; skipGuard: false; discardChanges?: undefined }
  | { shouldContinue: true; skipGuard: true; discardChanges: () => void };

export function getGlobalAiAssistantRoutePathname(path: string) {
  return getPathnameFromRoute(path);
}

export async function confirmGlobalAiAssistantRedirect({
  customDashContext,
  currentPathname,
  targetPath,
  confirmLeave,
}: ConfirmGlobalAiAssistantRedirectArgs): Promise<ConfirmGlobalAiAssistantRedirectResult> {
  const shouldConfirm = (() => {
    if (
      !customDashContext.isInCustomDashEditMode ||
      !customDashContext.hasUnsavedChanges
    ) {
      return false;
    }
    if (!targetPath) {
      return true;
    }

    const targetPathname = getPathnameFromRoute(targetPath);
    if (targetPathname === currentPathname) {
      return false;
    }

    const normalize = (pathname: string) => pathname.replace(/\/edit$/, '');
    // Mirrors useNavigationGuard: leaving an edit URL should prompt even
    // when the target is the same page's non-edit URL.
    return (
      normalize(currentPathname) !== normalize(targetPathname) ||
      currentPathname.endsWith('/edit')
    );
  })();

  if (!shouldConfirm) {
    return { shouldContinue: true, skipGuard: false };
  }

  const didConfirm = await confirmLeave(UNSAVED_CHANGES_MESSAGE);
  if (!didConfirm) {
    return { shouldContinue: false, skipGuard: false };
  }

  return customDashContext.discardChanges
    ? {
        shouldContinue: true,
        skipGuard: true,
        discardChanges: customDashContext.discardChanges,
      }
    : { shouldContinue: true, skipGuard: false };
}
